/*
 * Critical Design
 * Do not modify this section without explicit permission
 * 
 * Login page that handles:
 * - Social authentication (Apple, Google, Microsoft)
 * - Loading states
 * - Error handling
 * - Responsive design
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signInWithProvider, signInWithEmail, signInAnonymously } = useAuth();
  const navigate = useNavigate();

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      
      // Log the attempt for debugging
      console.log('Attempting login with:', email);
      
      const result = await signInWithEmail(email, password);
      console.log('Login result:', result);

      if (result?.user) {
        // Wait a bit for Firebase auth state to update
        await new Promise(resolve => setTimeout(resolve, 500));
        navigate('/', { replace: true });
      } else {
        console.error('No user returned from signInWithEmail');
        setError('Failed to sign in. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error.code, error.message);
      // Map Firebase error codes to user-friendly messages
      const errorMessage = {
        'auth/invalid-credential': 'Invalid email or password. Please try again.',
        'auth/user-not-found': 'No account found with this email.',
        'auth/wrong-password': 'Incorrect password.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/user-disabled': 'This account has been disabled.',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later.'
      }[error.code] || 'Failed to sign in. Please try again.';
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    try {
      setError('');
      setLoading(true);
      await signInWithProvider(provider);
      navigate('/');
    } catch (error) {
      setError('Failed to sign in. Please try again.');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnonymousLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await signInAnonymously();
      
      // Wait for Firebase auth state to update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      navigate('/');
    } catch (error) {
      setError('Failed to sign in anonymously. Please try again.');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-page dark:bg-page-dark">
      <div className="max-w-md w-full mx-4 p-8 rounded-lg shadow-lg bg-surface dark:bg-surface-dark">
        <h2 className="text-2xl font-bold mb-6 text-center text-text-primary dark:text-text-primary-dark">
          Sign In
        </h2>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-status-error-bg dark:bg-status-error-bg-dark text-status-error-text dark:text-status-error-text-dark">
            {error}
          </div>
        )}

        <form onSubmit={handleEmailLogin} className="space-y-4 mb-6">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full p-3 rounded-lg bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark border-input-border dark:border-input-border-dark focus:ring-2 focus:ring-button focus:border-transparent"
              required
            />
          </div>
          <div className="mb-6 relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full p-3 rounded-lg bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark border-input-border dark:border-input-border-dark focus:ring-2 focus:ring-button focus:border-transparent"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary dark:text-text-tertiary-dark hover:text-text-secondary dark:hover:text-text-secondary-dark"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full p-3 rounded-lg bg-button hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark text-text-primary-dark transition-colors disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="text-sm text-center mb-6 text-text-tertiary dark:text-text-tertiary-dark">
          <Link to="/forgot-password" className="text-text-link dark:text-text-link-dark hover:text-text-link-hover dark:hover:text-text-link-hover-dark">
            Forgot Password?
          </Link>
        </p>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-default dark:border-default-dark"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-surface dark:bg-surface-dark text-text-tertiary dark:text-text-tertiary-dark">
              Or continue with
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <button
            onClick={() => handleSocialLogin('google')}
            disabled={loading}
            className="w-full p-3 rounded-lg flex items-center justify-center gap-3 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark border-default dark:border-default-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
              />
            </svg>
            Continue with Google
          </button>

          <button
            onClick={handleAnonymousLogin}
            disabled={loading}
            className="w-full p-3 rounded-lg flex items-center justify-center gap-3 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark border-default dark:border-default-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="7" r="4"/>
              <path d="M5.338 19h13.324A2.338 2.338 0 0021 16.662v-.324A6.338 6.338 0 0014.662 10H9.338A6.338 6.338 0 003 16.338v.324A2.338 2.338 0 005.338 19z"/>
            </svg>
            Continue as Guest
          </button>
        </div>

        <p className="mt-8 text-sm text-center text-text-tertiary dark:text-text-tertiary-dark">
          Don't have an account?{' '}
          <Link to="/signup" className="text-text-link dark:text-text-link-dark hover:text-text-link-hover dark:hover:text-text-link-hover-dark">
            Sign Up
          </Link>
        </p>

        <p className="mt-4 text-sm text-center text-text-tertiary dark:text-text-tertiary-dark">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Login; 